// components/CommandPalette/utils/commandExecutor.ts
import { toBackendSemesterCode, fromBackendSemesterCode } from '../../../utils/semesterUtils';
import { dispatchAskChatbotHelp } from '../../../utils/appEvents';
import type { ParsedCommand, ParsedCommandRequirements } from './commandParser';

interface CommandCourse {
  code: string;
  title?: string;
  credits?: number;
  department_name?: string;
  description?: string;
  gen_ed_area?: string | string[] | null;
  gen_ed_requirements?: string | string[] | null;
  gened_requirements?: string | string[] | null;
  grade_data?: {
    average_gpa?: number | null;
    difficulty?: string | null;
  } | null;
  prerequisites_raw?: string | null;
  prerequisites?: PrereqNode[] | null;
  offered_fall?: boolean;
  offered_spring?: boolean;
  offered_summer?: boolean;
  offered_winter?: boolean;
}

interface PrereqNode {
  course?: string;
  and?: PrereqNode[];
  or?: PrereqNode[];
}

/**
 * A declared program on a plan. The persisted shape is `{program, track}`
 * (see academic_plan/plan_crud) — `code`/`name` appear in API-shaped payloads
 * and older fixtures. Both are accepted; the label helpers below fall back
 * across them (before F2 the CommandContext arrived through an
 * `as unknown as` cast, so the `{program}` shape silently fell through to
 * JSON.stringify in LIST/STATUS output).
 */
interface ProgramRef {
  code?: string;
  name?: string;
  program?: string;
  track?: string;
}

type ProgramRefOrString = ProgramRef | string;

interface CommandPlan {
  semesters: Record<string, CommandCourse[]>;
  bookmarkedCourses: CommandCourse[];
  majors?: ProgramRefOrString[];
  minors?: ProgramRefOrString[];
  citations?: ProgramRefOrString[];
}

interface PlaceholderData {
  title: string;
  credits: number;
  placeholderType: 'elective' | 'genEd';
  requirements: ParsedCommandRequirements;
  notes: string;
}

export interface CommandContext {
  academicPlan: CommandPlan;
  onAddCourse?: (course: CommandCourse, semester: string) => unknown;
  onRemoveCourse?: (semester: string, courseIndex: number, skipConfirmation?: boolean) => unknown;
  onMoveCourse?: (
    course: CommandCourse,
    fromSemester: string,
    toSemester: string,
    fromIndex: number
  ) => unknown;
  onAddToBookmarks?: (course: CommandCourse) => unknown;
  onRemoveFromBookmarks?: (courseIndex: number) => Promise<unknown> | unknown;
  onCreatePlaceholder?: (placeholder: PlaceholderData, semester: string | null) => unknown;
  onClearSemester?: (semester: string) => unknown;
  onClearAll?: () => unknown;
  courses: CommandCourse[];
  onSearchCourses?: (query: string) => Promise<CommandCourse[]>;
  /** True if this institution has a grade-history data source. When false,
   *  handlers omit grade-related output (e.g. INFO's "Average GPA" line).
   *  Undefined defaults to true for backward compatibility with callers that
   *  predate the institution-config wiring. Mirrors `hasGradeData` from
   *  `InstitutionConfigContext` — see SuggestionCard for the rendering twin. */
  hasGradeData?: boolean;
  /** Per-institution sample course codes used in the HELP command's
   *  "Course format" line. Phase 6.5 — sourced by callers from
   *  `useInstitutionTheme().exampleCourseCodes`. Falls back to UMD codes
   *  in `handleHelp` when undefined. */
  exampleCourseCodes?: string[];
}

export interface CommandResult {
  success: boolean;
  message: string;
  autoClose?: boolean;
}

function isTransferCreditsSemester(semester: string | null | undefined): boolean {
  const normalized = semester?.toLowerCase();
  const backendCode = semester ? toBackendSemesterCode(semester) : null;

  return (
    normalized === 'transfer' ||
    normalized === 'transfers' ||
    normalized === 'ap' ||
    normalized === 'aps' ||
    backendCode === '000008'
  );
}

function getTransferCreditsErrorMessage(): string {
  return 'Transfer Credits can only be managed via AP Credits. Use the "Manage AP Credits" option in the Transfer Credits section instead.';
}

/**
 * Collapse a typed course code to the form two spellings of one code share:
 * uppercase, no spaces, and none of the punctuation the retired UC Berkeley
 * guide used inside subjects (`L&S 1`, `A,RESEC 201`). `COMPSCI61A`,
 * `compsci 61a` and `COMPSCI 61A` all become `COMPSCI61A`; `MEC ENG C85`
 * and `MECENG C85` both become `MECENGC85`.
 *
 * The per-slug parsers deliberately admit these typed forms "for the
 * backend to canonicalize", but the executor compared codes with `===`
 * against the canonical catalog spelling, so every such input ended in
 * "Course … not found" (W56 #537 review; doc 56 §8 #67, the #323 class).
 * This is a comparison key only — the catalog's own spelling is what gets
 * returned and shown.
 */
export function normalizeTypedCode(code: string): string {
  return code.toUpperCase().replace(/[\s,&]/g, '');
}

function sameCourseCode(a: string, b: string): boolean {
  return a === b || normalizeTypedCode(a) === normalizeTypedCode(b);
}

export async function findCourseByCode(
  courseCode: string,
  courses: CommandCourse[],
  onSearchCourses?: (query: string) => Promise<CommandCourse[]>
): Promise<CommandCourse | null> {
  let courseData =
    courses.find((c) => c.code === courseCode) ??
    courses.find((c) => sameCourseCode(c.code, courseCode));
  if (!courseData && onSearchCourses) {
    try {
      const searchResults = await onSearchCourses(courseCode);
      courseData =
        searchResults?.find((c) => c.code === courseCode) ??
        searchResults?.find((c) => sameCourseCode(c.code, courseCode));
    } catch (error) {
      console.error('Error searching for course:', error);
    }
  }
  return courseData || null;
}

type Handler = (cmd: ParsedCommand, ctx: CommandContext) => CommandResult | Promise<CommandResult>;

const COMMAND_HANDLERS: Record<string, Handler> = {
  ADD: (cmd, ctx) => handleAddCourse(cmd as Extract<ParsedCommand, { type: 'ADD' }>, ctx),
  REMOVE: (cmd, ctx) => handleRemoveCourse(cmd as Extract<ParsedCommand, { type: 'REMOVE' }>, ctx),
  MOVE: (cmd, ctx) => handleMoveCourse(cmd as Extract<ParsedCommand, { type: 'MOVE' }>, ctx),
  BOOKMARK: (cmd, ctx) => handleBookmark(cmd as Extract<ParsedCommand, { type: 'BOOKMARK' }>, ctx),
  UNBOOKMARK: (cmd, ctx) =>
    handleUnbookmark(cmd as Extract<ParsedCommand, { type: 'UNBOOKMARK' }>, ctx),
  CREATE_PLACEHOLDER: (cmd, ctx) =>
    handleCreatePlaceholder(cmd as Extract<ParsedCommand, { type: 'CREATE_PLACEHOLDER' }>, ctx),
  ACCEPT: (cmd) => handleAcceptSuggestion(cmd as Extract<ParsedCommand, { type: 'ACCEPT' }>),
  REJECT: (cmd) => handleRejectSuggestion(cmd as Extract<ParsedCommand, { type: 'REJECT' }>),
  CLEAR_SEMESTER: (cmd, ctx) =>
    handleClearSemester(cmd as Extract<ParsedCommand, { type: 'CLEAR_SEMESTER' }>, ctx),
  ADD_SEMESTER: (cmd) =>
    handleAddSemester(cmd as Extract<ParsedCommand, { type: 'ADD_SEMESTER' }>),
  REMOVE_SEMESTER: (cmd) =>
    handleRemoveSemester(cmd as Extract<ParsedCommand, { type: 'REMOVE_SEMESTER' }>),
  ADD_MAJOR: (cmd) =>
    handleAddProgram(cmd as Extract<ParsedCommand, { type: 'ADD_MAJOR' }>, 'major'),
  REMOVE_MAJOR: (cmd) =>
    handleRemoveProgram(cmd as Extract<ParsedCommand, { type: 'REMOVE_MAJOR' }>, 'major'),
  ADD_MINOR: (cmd) =>
    handleAddProgram(cmd as Extract<ParsedCommand, { type: 'ADD_MINOR' }>, 'minor'),
  REMOVE_MINOR: (cmd) =>
    handleRemoveProgram(cmd as Extract<ParsedCommand, { type: 'REMOVE_MINOR' }>, 'minor'),
  ADD_CITATION: (cmd) =>
    handleAddProgram(cmd as Extract<ParsedCommand, { type: 'ADD_CITATION' }>, 'citation'),
  REMOVE_CITATION: (cmd) =>
    handleRemoveProgram(cmd as Extract<ParsedCommand, { type: 'REMOVE_CITATION' }>, 'citation'),
  RENAME_PLAN: (cmd) => handleRenamePlan(cmd as Extract<ParsedCommand, { type: 'RENAME_PLAN' }>),
  GENERATE_PLAN: () => handleGeneratePlan(),
  SUGGEST: () => handleSuggest(),
  STRATEGY: (cmd) => handleStrategy(cmd as Extract<ParsedCommand, { type: 'STRATEGY' }>),
  PIN: (cmd) => handlePin(cmd as Extract<ParsedCommand, { type: 'PIN' }>),
  UNPIN: (cmd) => handleUnpin(cmd as Extract<ParsedCommand, { type: 'UNPIN' }>),
  PREFER: (cmd) => handlePrefer(cmd as Extract<ParsedCommand, { type: 'PREFER' }>),
  CLEAR_PREFERENCES: () => handleClearPreferences(),
  EXPLAIN: (cmd) => handleExplain(cmd as Extract<ParsedCommand, { type: 'EXPLAIN' }>),
  WHAT_IF: (cmd) => handleWhatIf(cmd as Extract<ParsedCommand, { type: 'WHAT_IF' }>),
  PREREQS: (cmd, ctx) => handlePrereqs(cmd as Extract<ParsedCommand, { type: 'PREREQS' }>, ctx),
  AVAILABLE: (cmd, ctx) =>
    handleAvailable(cmd as Extract<ParsedCommand, { type: 'AVAILABLE' }>, ctx),
  INFO: (cmd, ctx) => handleInfo(cmd as Extract<ParsedCommand, { type: 'INFO' }>, ctx),
  SEARCH: (cmd, ctx) => handleSearch(cmd as Extract<ParsedCommand, { type: 'SEARCH' }>, ctx),
  STATUS: (_cmd, ctx) => handleStatus(ctx),
  CLEAR_SUGGESTIONS: () => handleClearSuggestions(),
  CLEAR_ALL: (_cmd, ctx) => handleClearAll(ctx),
  LIST: (cmd, ctx) => handleList(cmd as Extract<ParsedCommand, { type: 'LIST' }>, ctx),
  HELP: (_cmd, ctx) => handleHelp(ctx),
};

export async function executeCommand(
  parsedCommand: ParsedCommand,
  context: CommandContext
): Promise<CommandResult> {
  const handler = COMMAND_HANDLERS[parsedCommand.type];
  if (!handler) {
    return { success: false, message: 'Unknown command type' };
  }
  return handler(parsedCommand, context);
}

async function handleAddCourse(
  command: Extract<ParsedCommand, { type: 'ADD' }>,
  context: CommandContext
): Promise<CommandResult> {
  const { courseCode, semester } = command;
  const { academicPlan, onAddCourse, courses, onSearchCourses } = context;

  if (isTransferCreditsSemester(semester)) {
    return { success: false, message: getTransferCreditsErrorMessage() };
  }

  if (!semester) {
    return {
      success: false,
      message: `Invalid semester format. Use format like "fall-2025" or "F25"`,
    };
  }

  const numericSemester = toBackendSemesterCode(semester);

  if (!Object.prototype.hasOwnProperty.call(academicPlan.semesters, numericSemester)) {
    return {
      success: false,
      message: `Semester "${semester}" not found. Use format like "fall-2025" or "F25"`,
    };
  }

  const semesterCourses = academicPlan.semesters[numericSemester];
  if (semesterCourses.some((c) => c.code === courseCode)) {
    return {
      success: false,
      message: `${courseCode} already exists in ${semester}`,
    };
  }

  const courseData = await findCourseByCode(courseCode, courses, onSearchCourses);
  if (!courseData) {
    return {
      success: false,
      message: `Course ${courseCode} not found. Please check the course code.`,
    };
  }

  if (onAddCourse) {
    onAddCourse(courseData, numericSemester);
  }

  return {
    success: true,
    message: `Added ${courseCode} to ${semester}`,
    autoClose: false,
  };
}

async function handleRemoveCourse(
  command: Extract<ParsedCommand, { type: 'REMOVE' }>,
  context: CommandContext
): Promise<CommandResult> {
  const { courseCode, semester } = command;
  const { academicPlan, onRemoveCourse } = context;

  if (isTransferCreditsSemester(semester)) {
    return { success: false, message: getTransferCreditsErrorMessage() };
  }

  if (!semester) {
    return { success: false, message: `Invalid semester format` };
  }

  const numericSemester = toBackendSemesterCode(semester);

  if (!Object.prototype.hasOwnProperty.call(academicPlan.semesters, numericSemester)) {
    return { success: false, message: `Semester "${semester}" not found` };
  }

  const courseIndex = academicPlan.semesters[numericSemester].findIndex(
    (c) => c.code === courseCode
  );

  if (courseIndex === -1) {
    return { success: false, message: `${courseCode} not found in ${semester}` };
  }

  if (onRemoveCourse) {
    onRemoveCourse(numericSemester, courseIndex);
  }

  return {
    success: true,
    message: `Removed ${courseCode} from ${semester}`,
  };
}

async function handleMoveCourse(
  command: Extract<ParsedCommand, { type: 'MOVE' }>,
  context: CommandContext
): Promise<CommandResult> {
  const { courseCode, fromSemester, toSemester } = command;
  const { academicPlan, onMoveCourse } = context;

  if (isTransferCreditsSemester(fromSemester)) {
    return {
      success: false,
      message: `Cannot move courses from Transfer Credits. ${getTransferCreditsErrorMessage()}`,
    };
  }

  if (isTransferCreditsSemester(toSemester)) {
    return {
      success: false,
      message: `Cannot move courses to Transfer Credits. ${getTransferCreditsErrorMessage()}`,
    };
  }

  if (!fromSemester || !toSemester) {
    return { success: false, message: `Invalid semester format` };
  }

  const numericFromSemester = toBackendSemesterCode(fromSemester);
  const numericToSemester = toBackendSemesterCode(toSemester);

  if (!Object.prototype.hasOwnProperty.call(academicPlan.semesters, numericFromSemester)) {
    return { success: false, message: `Source semester "${fromSemester}" not found` };
  }

  if (!Object.prototype.hasOwnProperty.call(academicPlan.semesters, numericToSemester)) {
    return { success: false, message: `Target semester "${toSemester}" not found` };
  }

  const courseIndex = academicPlan.semesters[numericFromSemester].findIndex(
    (c) => c.code === courseCode
  );

  if (courseIndex === -1) {
    return { success: false, message: `${courseCode} not found in ${fromSemester}` };
  }

  if (academicPlan.semesters[numericToSemester].some((c) => c.code === courseCode)) {
    return { success: false, message: `${courseCode} already exists in ${toSemester}` };
  }

  const course = academicPlan.semesters[numericFromSemester][courseIndex];
  if (onMoveCourse) {
    onMoveCourse(course, numericFromSemester, numericToSemester, courseIndex);
  }

  return {
    success: true,
    message: `Moved ${courseCode} from ${fromSemester} to ${toSemester}`,
  };
}

async function handleBookmark(
  command: Extract<ParsedCommand, { type: 'BOOKMARK' }>,
  context: CommandContext
): Promise<CommandResult> {
  const { courseCode } = command;
  const { academicPlan, onAddToBookmarks, courses, onSearchCourses } = context;

  if (academicPlan.bookmarkedCourses.some((c) => c.code === courseCode)) {
    return { success: false, message: `${courseCode} is already bookmarked` };
  }

  const courseData = await findCourseByCode(courseCode, courses, onSearchCourses);
  if (!courseData) {
    return { success: false, message: `Course ${courseCode} not found` };
  }

  if (onAddToBookmarks) {
    onAddToBookmarks(courseData);
  }

  return { success: true, message: `Bookmarked ${courseCode}` };
}

async function handleUnbookmark(
  command: Extract<ParsedCommand, { type: 'UNBOOKMARK' }>,
  context: CommandContext
): Promise<CommandResult> {
  const { courseCode } = command;
  const { academicPlan, onRemoveFromBookmarks } = context;

  const bookmarkIndex = academicPlan.bookmarkedCourses.findIndex((c) => c.code === courseCode);

  if (bookmarkIndex === -1) {
    return { success: false, message: `${courseCode} is not bookmarked` };
  }

  try {
    if (onRemoveFromBookmarks) {
      await onRemoveFromBookmarks(bookmarkIndex);
    }
    return { success: true, message: `Removed ${courseCode} from bookmarks` };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    return { success: false, message: `Failed to remove ${courseCode} from bookmarks: ${message}` };
  }
}

async function handleCreatePlaceholder(
  command: Extract<ParsedCommand, { type: 'CREATE_PLACEHOLDER' }>,
  context: CommandContext
): Promise<CommandResult> {
  const { title, semester, requirements } = command;
  const { academicPlan, onCreatePlaceholder } = context;

  if (isTransferCreditsSemester(semester)) {
    return {
      success: false,
      message: `Cannot create placeholders in Transfer Credits. ${getTransferCreditsErrorMessage()}`,
    };
  }

  if (!semester) {
    return { success: false, message: `Invalid semester format` };
  }

  const numericSemester = toBackendSemesterCode(semester);

  if (!Object.prototype.hasOwnProperty.call(academicPlan.semesters, numericSemester)) {
    return { success: false, message: `Semester "${semester}" not found` };
  }

  const placeholderData: PlaceholderData = {
    title,
    credits: 3,
    placeholderType: requirements.department ? 'elective' : 'genEd',
    requirements,
    notes: 'Created via command',
  };

  if (onCreatePlaceholder) {
    onCreatePlaceholder(placeholderData, numericSemester);
  }

  return { success: true, message: `Created placeholder "${title}" in ${semester}` };
}

async function handleClearSemester(
  command: Extract<ParsedCommand, { type: 'CLEAR_SEMESTER' }>,
  context: CommandContext
): Promise<CommandResult> {
  const { semester } = command;
  const { academicPlan, onClearSemester } = context;

  if (isTransferCreditsSemester(semester)) {
    return {
      success: false,
      message: `Cannot clear Transfer Credits semester. ${getTransferCreditsErrorMessage()}`,
    };
  }

  if (!semester) {
    return { success: false, message: `Invalid semester format` };
  }

  const numericSemester = toBackendSemesterCode(semester);

  if (!Object.prototype.hasOwnProperty.call(academicPlan.semesters, numericSemester)) {
    return { success: false, message: `Semester "${semester}" not found` };
  }

  const courseCount = academicPlan.semesters[numericSemester].length;

  if (courseCount === 0) {
    return { success: false, message: `${semester} is already empty` };
  }

  if (onClearSemester) {
    onClearSemester(numericSemester);
  }

  return {
    success: true,
    message: `Cleared ${courseCount} courses from ${semester}`,
  };
}

function formatSemesterLabel(normalized: string | null | undefined): string {
  if (!normalized) return String(normalized);
  const [term, year] = normalized.split('-');
  return `${term.charAt(0).toUpperCase() + term.slice(1)} ${year}`;
}

function dispatchToChatbot(
  chatMessage: string,
  action: string,
  statusMessage: string
): CommandResult {
  dispatchAskChatbotHelp({ message: chatMessage, action });
  return { success: true, message: statusMessage };
}

function handleAcceptSuggestion(
  command: Extract<ParsedCommand, { type: 'ACCEPT' }>
): CommandResult {
  return dispatchToChatbot(
    `Accept the suggestion for ${command.courseCode}.`,
    'accept_suggestion',
    `Accepting suggestion ${command.courseCode} via chatbot...`
  );
}

function handleRejectSuggestion(
  command: Extract<ParsedCommand, { type: 'REJECT' }>
): CommandResult {
  return dispatchToChatbot(
    `Reject the suggestion for ${command.courseCode}.`,
    'reject_suggestion',
    `Rejecting suggestion ${command.courseCode} via chatbot...`
  );
}

function handleAddSemester(
  command: Extract<ParsedCommand, { type: 'ADD_SEMESTER' }>
): CommandResult {
  const { semester } = command;
  if (!semester) return { success: false, message: 'Invalid semester format. Use: ADD SEMESTER F26' };
  const label = formatSemesterLabel(semester);
  return dispatchToChatbot(
    `Add a ${label} semester to my plan.`,
    'add_semester',
    `Adding ${label} semester via chatbot...`
  );
}

function handleRemoveSemester(
  command: Extract<ParsedCommand, { type: 'REMOVE_SEMESTER' }>
): CommandResult {
  const { semester } = command;
  if (!semester)
    return { success: false, message: 'Invalid semester format. Use: REMOVE SEMESTER F26' };
  const label = formatSemesterLabel(semester);
  return dispatchToChatbot(
    `Remove the ${label} semester from my plan.`,
    'remove_semester',
    `Removing ${label} semester via chatbot...`
  );
}

function handleStrategy(
  command: Extract<ParsedCommand, { type: 'STRATEGY' }>
): CommandResult {
  return dispatchToChatbot(
    `Set my course load to ${command.strategy}.`,
    'update_strategy',
    `Updating strategy to ${command.strategy} via chatbot...`
  );
}

function handleSuggest(): CommandResult {
  return dispatchToChatbot(
    'Generate course suggestions for my plan.',
    'suggest_courses',
    'Generating course suggestions via chatbot...'
  );
}

function handleClearSuggestions(): CommandResult {
  return dispatchToChatbot(
    'Clear all course suggestions from my plan.',
    'clear_suggestions',
    'Clearing course suggestions via chatbot...'
  );
}

function handleAddProgram(
  command:
    | Extract<ParsedCommand, { type: 'ADD_MAJOR' }>
    | Extract<ParsedCommand, { type: 'ADD_MINOR' }>
    | Extract<ParsedCommand, { type: 'ADD_CITATION' }>,
  programType: 'major' | 'minor' | 'citation'
): CommandResult {
  const intentMap = { major: 'add_major', minor: 'add_minor', citation: 'add_citation' };
  return dispatchToChatbot(
    `Add the ${programType} ${command.programCode} to my plan.`,
    intentMap[programType],
    `Adding ${programType} ${command.programCode} via chatbot...`
  );
}

function handleRemoveProgram(
  command:
    | Extract<ParsedCommand, { type: 'REMOVE_MAJOR' }>
    | Extract<ParsedCommand, { type: 'REMOVE_MINOR' }>
    | Extract<ParsedCommand, { type: 'REMOVE_CITATION' }>,
  programType: 'major' | 'minor' | 'citation'
): CommandResult {
  const intentMap = {
    major: 'remove_major',
    minor: 'remove_minor',
    citation: 'remove_citation',
  };
  return dispatchToChatbot(
    `Remove the ${programType} ${command.programCode} from my plan.`,
    intentMap[programType],
    `Removing ${programType} ${command.programCode} via chatbot...`
  );
}

function handleRenamePlan(
  command: Extract<ParsedCommand, { type: 'RENAME_PLAN' }>
): CommandResult {
  return dispatchToChatbot(
    `Rename my plan to "${command.name}".`,
    'rename_plan',
    `Renaming plan to "${command.name}" via chatbot...`
  );
}

function handleGeneratePlan(): CommandResult {
  return dispatchToChatbot(
    'Generate an optimized plan for me.',
    'generate_plan',
    'Generating optimized plan via chatbot...'
  );
}

async function handleInfo(
  command: Extract<ParsedCommand, { type: 'INFO' }>,
  context: CommandContext
): Promise<CommandResult> {
  const { courseCode } = command;
  const { courses, onSearchCourses, hasGradeData } = context;

  const courseData = await findCourseByCode(courseCode, courses, onSearchCourses);
  if (!courseData) {
    return { success: false, message: `Course ${courseCode} not found.` };
  }

  const lines = [
    `${courseData.code} - ${courseData.title || 'No title'} (${courseData.credits || '?'} credits)`,
  ];
  if (courseData.department_name) lines.push(`Department: ${courseData.department_name}`);
  if (courseData.description) lines.push(`${courseData.description}`);
  const genEd =
    courseData.gen_ed_area || courseData.gen_ed_requirements || courseData.gened_requirements;
  if (genEd && (Array.isArray(genEd) ? genEd.length > 0 : true)) {
    lines.push(`Gen Ed: ${Array.isArray(genEd) ? genEd.join(', ') : genEd}`);
  }
  // Hide grade UI for institutions with no grade-data source. Undefined =
  // legacy caller → behave as before (show).
  const showGradeUI = hasGradeData !== false;
  if (showGradeUI && courseData.grade_data?.average_gpa) {
    lines.push(
      `Average GPA: ${courseData.grade_data.average_gpa.toFixed(2)} (${courseData.grade_data.difficulty || 'N/A'})`
    );
  }

  return { success: true, message: lines.join('\n') };
}

async function handleSearch(
  command: Extract<ParsedCommand, { type: 'SEARCH' }>,
  context: CommandContext
): Promise<CommandResult> {
  const { query } = command;
  const { onSearchCourses } = context;

  if (!onSearchCourses) {
    return { success: false, message: 'Course search not available.' };
  }

  try {
    const results = await onSearchCourses(query);
    if (!results || results.length === 0) {
      return { success: false, message: `No courses found for "${query}".` };
    }

    const lines = [`Found ${results.length} course${results.length > 1 ? 's' : ''}:`];
    results.slice(0, 10).forEach((c) => {
      lines.push(`  ${c.code} - ${c.title || ''} (${c.credits || '?'} cr)`);
    });
    if (results.length > 10) lines.push(`  ... and ${results.length - 10} more`);

    return { success: true, message: lines.join('\n') };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    return { success: false, message: `Search failed: ${message}` };
  }
}

function handlePin(command: Extract<ParsedCommand, { type: 'PIN' }>): CommandResult {
  const label = formatSemesterLabel(command.semester);
  return dispatchToChatbot(
    `Pin ${command.courseCode} in ${label} so the optimizer won't move it.`,
    'update_course_status',
    `Pinning ${command.courseCode} in ${label} via chatbot...`
  );
}

function handleUnpin(command: Extract<ParsedCommand, { type: 'UNPIN' }>): CommandResult {
  const label = formatSemesterLabel(command.semester);
  return dispatchToChatbot(
    `Unpin ${command.courseCode} in ${label} so the optimizer can move it.`,
    'update_course_status',
    `Unpinning ${command.courseCode} in ${label} via chatbot...`
  );
}

function handlePrefer(command: Extract<ParsedCommand, { type: 'PREFER' }>): CommandResult {
  return dispatchToChatbot(
    `Set my course preferences to: ${command.courses}`,
    'set_course_preferences',
    'Setting course preferences via chatbot...'
  );
}

function handleClearPreferences(): CommandResult {
  return dispatchToChatbot(
    'Clear all my course preferences.',
    'clear_course_preferences',
    'Clearing course preferences via chatbot...'
  );
}

function handleExplain(command: Extract<ParsedCommand, { type: 'EXPLAIN' }>): CommandResult {
  return dispatchToChatbot(
    `Explain why ${command.courseCode} was suggested.`,
    'explain_suggestion',
    `Explaining suggestion for ${command.courseCode} via chatbot...`
  );
}

function handleWhatIf(command: Extract<ParsedCommand, { type: 'WHAT_IF' }>): CommandResult {
  return dispatchToChatbot(
    `What if ${command.query}`,
    'what_if',
    `Analyzing "what if ${command.query}" via chatbot...`
  );
}

async function handlePrereqs(
  command: Extract<ParsedCommand, { type: 'PREREQS' }>,
  context: CommandContext
): Promise<CommandResult> {
  const { courseCode } = command;
  const { courses, onSearchCourses } = context;

  const courseData = await findCourseByCode(courseCode, courses, onSearchCourses);
  if (!courseData) {
    return { success: false, message: `Course ${courseCode} not found.` };
  }

  if (courseData.prerequisites_raw) {
    return {
      success: true,
      message: `${courseCode} prerequisites:\n${courseData.prerequisites_raw}`,
    };
  }

  if (courseData.prerequisites && courseData.prerequisites.length > 0) {
    return {
      success: true,
      message: `${courseCode} prerequisites:\n${formatPrereqTree(courseData.prerequisites)}`,
    };
  }

  return { success: true, message: `${courseCode} has no prerequisites.` };
}

function formatPrereqTree(prereqs: PrereqNode[] | null | undefined): string {
  if (!prereqs || prereqs.length === 0) return 'None';

  function walk(node: PrereqNode): string {
    if (node.course) return node.course;
    if (node.and) return node.and.map(walk).join(' AND ');
    if (node.or) return node.or.map(walk).join(' OR ');
    return JSON.stringify(node);
  }

  return prereqs.map(walk).join(', ');
}

async function handleAvailable(
  command: Extract<ParsedCommand, { type: 'AVAILABLE' }>,
  context: CommandContext
): Promise<CommandResult> {
  const { courseCode } = command;
  const { courses, onSearchCourses } = context;

  const courseData = await findCourseByCode(courseCode, courses, onSearchCourses);
  if (!courseData) {
    return { success: false, message: `Course ${courseCode} not found.` };
  }

  const semesters: string[] = [];
  if (courseData.offered_fall !== false) semesters.push('Fall');
  if (courseData.offered_spring !== false) semesters.push('Spring');
  if (courseData.offered_summer) semesters.push('Summer');
  if (courseData.offered_winter) semesters.push('Winter');

  if (semesters.length === 0) {
    return { success: true, message: `${courseCode} offering data not available.` };
  }

  return { success: true, message: `${courseCode} is offered in: ${semesters.join(', ')}` };
}

function programLabel(p: ProgramRefOrString): string {
  if (typeof p === 'string') return p;
  return p.name || p.program || p.code || JSON.stringify(p);
}

function programShort(p: ProgramRefOrString): string {
  if (typeof p === 'string') return p;
  return p.code || p.program || p.name || JSON.stringify(p);
}

function handleStatus(context: CommandContext): CommandResult {
  const { academicPlan } = context;

  const semesters = Object.entries(academicPlan.semesters).filter(([key]) => key !== '000008');

  let totalCourses = 0;
  let totalCredits = 0;
  const semesterLines: string[] = [];

  semesters.forEach(([key, courses]) => {
    const label = fromBackendSemesterCode(key);
    const credits = courses.reduce((sum, c) => sum + (c.credits || 0), 0);
    totalCourses += courses.length;
    totalCredits += credits;
    if (courses.length > 0) {
      semesterLines.push(`  ${label}: ${courses.length} courses, ${credits} credits`);
    }
  });

  const bookmarkCount = academicPlan.bookmarkedCourses?.length || 0;
  const programs: string[] = [];
  if (academicPlan.majors?.length)
    programs.push(`Majors: ${academicPlan.majors.map(programShort).join(', ')}`);
  if (academicPlan.minors?.length)
    programs.push(`Minors: ${academicPlan.minors.map(programShort).join(', ')}`);
  if (academicPlan.citations?.length)
    programs.push(`Citations: ${academicPlan.citations.map(programShort).join(', ')}`);

  const lines = ['Plan Status:'];
  if (programs.length > 0) lines.push(...programs);
  lines.push(
    `Total: ${totalCourses} courses, ${totalCredits} credits across ${semesters.length} semesters`
  );
  if (bookmarkCount > 0) lines.push(`Bookmarks: ${bookmarkCount}`);
  if (semesterLines.length > 0) {
    lines.push('');
    lines.push(...semesterLines);
  }

  return { success: true, message: lines.join('\n') };
}

async function handleClearAll(context: CommandContext): Promise<CommandResult> {
  const { onClearAll } = context;

  if (onClearAll) {
    onClearAll();
  }

  return {
    success: true,
    message: 'Cleared all courses from all semesters (Transfer Credits preserved)',
  };
}

async function handleList(
  command: Extract<ParsedCommand, { type: 'LIST' }>,
  context: CommandContext
): Promise<CommandResult> {
  const { what, semester } = command;

  switch (what) {
    case 'SEMESTERS': {
      const readableSemesters = Object.keys(context.academicPlan.semesters)
        .filter((sem) => sem !== '000008')
        .map((sem) => fromBackendSemesterCode(sem))
        .join(', ');
      return {
        success: true,
        message: `Available semesters: ${readableSemesters}\n\nNote: Transfer Credits are managed via AP Credits and cannot be modified by commands.`,
      };
    }

    case 'COURSES': {
      if (!semester) {
        return { success: false, message: 'Usage: LIST COURSES IN [SEMESTER]' };
      }
      const numericSemester = toBackendSemesterCode(semester);
      const semCourses = context.academicPlan.semesters[numericSemester];
      if (!semCourses) {
        return { success: false, message: `Semester "${semester}" not found` };
      }
      if (semCourses.length === 0) {
        return { success: true, message: `${semester} is empty.` };
      }
      const credits = semCourses.reduce((sum, c) => sum + (c.credits || 0), 0);
      const lines = [`${semester} (${semCourses.length} courses, ${credits} credits):`];
      semCourses.forEach((c) => {
        lines.push(`  ${c.code} - ${c.title || ''} (${c.credits || '?'} cr)`);
      });
      return { success: true, message: lines.join('\n') };
    }

    case 'BOOKMARKS': {
      const bookmarks = context.academicPlan.bookmarkedCourses || [];
      if (bookmarks.length === 0) {
        return { success: true, message: 'No bookmarked courses.' };
      }
      const lines = [`Bookmarked courses (${bookmarks.length}):`];
      bookmarks.forEach((c) => {
        lines.push(`  ${c.code} - ${c.title || ''} (${c.credits || '?'} cr)`);
      });
      return { success: true, message: lines.join('\n') };
    }

    case 'PROGRAMS': {
      const plan = context.academicPlan;
      const lines = ['Declared programs:'];
      const majors = plan.majors || [];
      const minors = plan.minors || [];
      const citations = plan.citations || [];
      if (majors.length > 0) lines.push(`  Majors: ${majors.map(programLabel).join(', ')}`);
      if (minors.length > 0) lines.push(`  Minors: ${minors.map(programLabel).join(', ')}`);
      if (citations.length > 0) lines.push(`  Citations: ${citations.map(programLabel).join(', ')}`);
      if (majors.length === 0 && minors.length === 0 && citations.length === 0) {
        lines.push('  No programs declared.');
      }
      return { success: true, message: lines.join('\n') };
    }

    default:
      return {
        success: false,
        message: 'LIST supports: SEMESTERS, COURSES IN [SEMESTER], BOOKMARKS, PROGRAMS',
      };
  }
}

function handleHelp(ctx: CommandContext): CommandResult {
  // Phase 6.5: per-institution example codes for the "Course format" hint.
  // Falls back to UMD codes when undefined (older callers that don't pass
  // the new context field).
  const examples = (ctx.exampleCourseCodes && ctx.exampleCourseCodes.length > 0)
    ? ctx.exampleCourseCodes.slice(0, 3).join(', ')
    : 'CMSC216, MATH140, ENGL101';
  const helpText = `
Available commands:

Course Operations:
  ADD [COURSE] TO [SEMESTER]                  Add course to semester
  REMOVE [COURSE] FROM [SEMESTER]             Remove course
  MOVE [COURSE] FROM [SEM] TO [SEM]           Move course between semesters
  PIN [COURSE] IN [SEMESTER]                  Lock course in place
  UNPIN [COURSE] IN [SEMESTER]                Unlock a pinned course
  ACCEPT [COURSE]                             Accept a suggestion
  REJECT [COURSE]                             Reject a suggestion
  EXPLAIN [COURSE]                            Explain why a course was suggested
  BOOKMARK [COURSE]                           Add to bookmarks
  UNBOOKMARK [COURSE]                         Remove from bookmarks

Program Operations:
  ADD MAJOR [CODE]                            Add a major
  REMOVE MAJOR [CODE]                         Remove a major
  ADD MINOR [CODE]                            Add a minor
  REMOVE MINOR [CODE]                         Remove a minor
  ADD CITATION [CODE]                         Add a citation
  REMOVE CITATION [CODE]                      Remove a citation

Semester & Plan Operations:
  ADD SEMESTER [SEMESTER]                     Add a new semester
  REMOVE SEMESTER [SEMESTER]                  Remove a semester
  CREATE PLACEHOLDER "[TITLE]" IN [SEM]       Create placeholder
  CLEAR [SEMESTER]                            Clear all courses from semester
  CLEAR ALL                                   Clear entire plan
  RENAME PLAN "[NAME]"                        Rename the plan

Generation & Suggestions:
  GENERATE PLAN                               Generate an optimized plan
  SUGGEST                                     Generate course suggestions
  CLEAR SUGGESTIONS                           Clear all suggestions
  STRATEGY [LIGHTER|NORMAL|HEAVIER]           Set course load
  PREFER [COURSES]                            Set course preferences
  CLEAR PREFERENCES                           Clear course preferences
  WHAT IF [SCENARIO]                          What-if analysis

Information:
  INFO [COURSE]                               Show course details
  PREREQS [COURSE]                            Show prerequisites
  AVAILABLE [COURSE]                          Show offering semesters
  SEARCH [QUERY]                              Search for courses
  STATUS                                      Show plan summary
  LIST SEMESTERS                              Show available semesters
  LIST COURSES IN [SEMESTER]                  Show courses in a semester
  LIST BOOKMARKS                              Show bookmarked courses
  LIST PROGRAMS                               Show declared programs
  HELP                                        Show this help

Semester formats: fall-2025, F25, FALL-2025
Course format: ${examples}
  `.trim();

  return { success: true, message: helpText };
}
